import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import axios from "axios";
import { CREWAI_AGENTS_URL } from "../constants/apiUrl";
import { ACCESS_TOKEN } from "../constants/constants";
import { getCookie } from "../helpers/jwt";
import { uuidToBase64Url } from "../helpers/shared";

const AgentsList = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [accessToken] = useState(getCookie(ACCESS_TOKEN));

  const cardStyles = [
    {
      bg: "bg-green-50",
      border: "border-green-200",
      tile: "bg-gradient-to-br from-emerald-400 to-teal-500",
      ring: "group-hover:ring-emerald-200",
    },
    {
      bg: "bg-red-50",
      border: "border-red-200",
      tile: "bg-gradient-to-br from-rose-400 to-pink-500",
      ring: "group-hover:ring-rose-200",
    },
    {
      bg: "bg-blue-50",
      border: "border-blue-200",
      tile: "bg-gradient-to-br from-sky-400 to-indigo-500",
      ring: "group-hover:ring-sky-200",
    },
    {
      bg: "bg-yellow-50",
      border: "border-yellow-200",
      tile: "bg-gradient-to-br from-amber-400 to-orange-500",
      ring: "group-hover:ring-amber-200",
    },
  ];

  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash);
  };

  // Assign a color index per agent: seed from the agent id (so it's stable/scattered),
  // but never repeat the previous card's color so no two neighbours match.
  const getColorIndexes = (items) => {
    const indexes = [];
    let prev = -1;
    items.forEach((item, i) => {
      const seed = hashString(
        String(item?.id ?? item?.agent_config?.name ?? i),
      );
      let idx = seed % cardStyles.length;
      if (idx === prev) {
        idx = (idx + 1) % cardStyles.length;
      }
      indexes.push(idx);
      prev = idx;
    });
    return indexes;
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${CREWAI_AGENTS_URL}/agents`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      setAgents(response.data || []);
    } catch (err) {
      console.error("Error fetching agents:", err);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAgents = agents.filter((agent) =>
    agent?.agent_config?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 text-gray-500">
        <Loader2 className="text-theme h-9 w-9 animate-spin" />
        <span className="text-sm font-medium">Loading agents…</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold">Agents</h2>

        <div className="relative w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search agents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="focus-theme w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-700 shadow-sm outline-none transition"
          />
        </div>
      </div>

      {filteredAgents.length === 0 ? (
        <div className="w-full py-10 text-center text-gray-500">
          No agents found.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {(() => {
            const colorIndexes = getColorIndexes(filteredAgents);
            return filteredAgents.map((agent, index) => {
              const { bg, border, tile, ring } =
                cardStyles[colorIndexes[index]];
              const name = agent?.agent_config?.name || "Untitled Agent";
              const initial = (name.trim()[0] || "?").toUpperCase();

              return (
                <div
                  key={agent?.id}
                  className={`group flex flex-col rounded-2xl border ${bg} ${border} p-5 shadow-sm ring-1 ring-transparent transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:ring-2 ${ring}`}
                >
                  {/* Body */}
                  <h3
                    className="mb-1 line-clamp-1 text-base font-semibold text-gray-900"
                    title={name}
                  >
                    {name}
                  </h3>
                  {agent?.agent_config?.role && (
                    <p
                      className="mb-1 line-clamp-1 text-xs font-medium text-gray-500"
                      title={agent.agent_config.role}
                    >
                      {agent.agent_config.role}
                    </p>
                  )}
                  {agent?.agent_config?.backstory && (
                    <p className="mb-4 line-clamp-2 text-sm text-gray-500">
                      {agent.agent_config.backstory}
                    </p>
                  )}

                  {/* Footer: action bar */}
                  <div className="mt-auto flex items-center justify-end gap-2 border-t border-black/5 pt-3">
                    <Link
                      to={`/agent-chat?agentId=${uuidToBase64Url(agent?.id)}`}
                      className="bg-theme inline-flex items-center justify-center rounded-md px-3.5 py-1.5 text-sm font-medium text-white shadow-sm transition hover:opacity-90"
                    >
                      Run
                    </Link>
                    <Link
                      to={`/?agentId=${uuidToBase64Url(agent?.id)}`}
                      className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3.5 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
                    >
                      View App
                    </Link>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
};

export default AgentsList;
